import { FFmpeg } from '@ffmpeg/ffmpeg';
import { toBlobURL } from '@ffmpeg/util';
import { drawFrame } from './drawFrame';
import type { CaptionCue, ReelProject } from '@/types';

const FFMPEG_CORE_VERSION = '0.12.6';
const FFMPEG_CORE_CDN_BASE = `https://cdn.jsdelivr.net/npm/@ffmpeg/core@${FFMPEG_CORE_VERSION}/dist/umd`;

let coreUrlsPromise: Promise<{ coreURL: string; wasmURL: string }> | null = null;
function getCoreUrls() {
  if (!coreUrlsPromise) {
    coreUrlsPromise = Promise.all([
      toBlobURL(`${FFMPEG_CORE_CDN_BASE}/ffmpeg-core.js`, 'text/javascript'),
      toBlobURL(`${FFMPEG_CORE_CDN_BASE}/ffmpeg-core.wasm`, 'application/wasm'),
    ]).then(([coreURL, wasmURL]) => ({ coreURL, wasmURL }));
  }
  return coreUrlsPromise;
}

const QUALITY_PRESETS: Record<'draft' | 'standard' | 'high', { preset: string; crf: number }> = {
  draft: { preset: 'ultrafast', crf: 30 },
  standard: { preset: 'veryfast', crf: 23 },
  high: { preset: 'slow', crf: 18 },
};

export interface RunExportParams {
  project: ReelProject;
  captionCues: CaptionCue[];
  images: Array<{ mediaId: string; bitmap: ImageBitmap }>;
  watermarkBitmap: ImageBitmap | null;
  audioWav: ArrayBuffer | null;
  totalDurationSeconds: number;
  widthPx: number;
  heightPx: number;
  frameRate: number;
  quality: 'draft' | 'standard' | 'high';
  onProgress?: (progress: number, stage: string) => void;
  onLog?: (message: string) => void;
  cancelToken: { cancelled: boolean };
}

export class RenderCancelledError extends Error {
  constructor() {
    super('Render was cancelled.');
    this.name = 'RenderCancelledError';
  }
}

/** Renders a project to an MP4 Blob: draws every frame with the shared drawFrame renderer, then muxes with FFmpeg. */
export async function runFfmpegExport(params: RunExportParams): Promise<Blob> {
  const { coreURL, wasmURL } = await getCoreUrls();
  const instance = new FFmpeg();

  instance.on('log', ({ message }) => params.onLog?.(message));
  instance.on('progress', ({ progress }) => {
    const clamped = Math.min(1, Math.max(0, progress));
    params.onProgress?.(55 + clamped * 40, 'encoding');
  });

  try {
    await instance.load({ coreURL, wasmURL });

    const imageMap = new Map(params.images.map((i) => [i.mediaId, i.bitmap]));
    const canvas =
      typeof OffscreenCanvas !== 'undefined'
        ? new OffscreenCanvas(params.widthPx, params.heightPx)
        : Object.assign(document.createElement('canvas'), { width: params.widthPx, height: params.heightPx });
    const ctx = canvas.getContext('2d') as CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D | null;
    if (!ctx) throw new Error('2D canvas rendering is not supported in this browser.');

    const totalFrames = Math.max(1, Math.round(params.totalDurationSeconds * params.frameRate));
    const digits = 5;

    for (let frame = 0; frame < totalFrames; frame++) {
      if (params.cancelToken.cancelled) throw new RenderCancelledError();

      const time = frame / params.frameRate;
      drawFrame({
        ctx,
        canvasWidth: params.widthPx,
        canvasHeight: params.heightPx,
        time,
        project: params.project,
        imageBitmaps: imageMap,
        watermarkBitmap: params.watermarkBitmap,
        captionCues: params.captionCues,
      });

      const frameBlob =
        'convertToBlob' in canvas
          ? await canvas.convertToBlob({ type: 'image/jpeg', quality: 0.92 })
          : await new Promise<Blob>((resolve, reject) =>
              (canvas as HTMLCanvasElement).toBlob((b) => (b ? resolve(b) : reject(new Error('Canvas encoding failed.'))), 'image/jpeg', 0.92)
            );
      const bytes = new Uint8Array(await frameBlob.arrayBuffer());
      await instance.writeFile(`frame${String(frame).padStart(digits, '0')}.jpg`, bytes);

      if (frame % 5 === 0 || frame === totalFrames - 1) {
        params.onProgress?.((frame / totalFrames) * 50, 'rendering-frames');
      }

      // Yield to the browser every few frames so the UI (progress bar, Cancel button) stays
      // responsive — frame drawing/encoding happens on the main thread, unlike the actual
      // FFmpeg mux step below, which runs inside FFmpeg's own internal worker.
      if (frame % 8 === 0) await new Promise((resolve) => setTimeout(resolve, 0));
    }

    if (params.cancelToken.cancelled) throw new RenderCancelledError();

    if (params.audioWav) {
      await instance.writeFile('audio.wav', new Uint8Array(params.audioWav));
    }

    const { preset, crf } = QUALITY_PRESETS[params.quality];
    const args: string[] = ['-framerate', String(params.frameRate), '-i', `frame%0${digits}d.jpg`];
    if (params.audioWav) args.push('-i', 'audio.wav');
    args.push(
      '-c:v', 'libx264',
      '-pix_fmt', 'yuv420p',
      '-profile:v', 'main',
      '-preset', preset,
      '-crf', String(crf),
      '-r', String(params.frameRate)
    );
    if (params.audioWav) args.push('-c:a', 'aac', '-b:a', '192k', '-shortest');
    args.push('-movflags', '+faststart', 'output.mp4');

    params.onProgress?.(52, 'encoding');
    await instance.exec(args);

    if (params.cancelToken.cancelled) throw new RenderCancelledError();

    const data = await instance.readFile('output.mp4');
    const resultBlob = new Blob([new Uint8Array(data as Uint8Array)], { type: 'video/mp4' });
    params.onProgress?.(100, 'done');
    return resultBlob;
  } finally {
    try {
      instance.terminate();
    } catch {
      /* already terminated */
    }
  }
}
